import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  InsightsCard,
  InsightsChatTurn,
  InsightsConversationSummary,
  InsightsDomain,
  InsightsQueryFilters,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { TabNav } from '../../components/ui/TabNav.js';
import { Icon } from '../../components/ui/Icon.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import {
  insightsKeys,
  sendInsightsChatStream,
  setInsightsFeedback,
  useDeleteInsightsConversation,
  useDeleteSavedInsightsQuestion,
  useInsightsConversation,
  useInsightsConversations,
  useInsightsStarters,
  useInsightsStudioStatus,
  useRenameInsightsConversation,
  useSaveInsightsQuestion,
  useSavedInsightsQuestions,
} from './api.js';
import { InsightsCardView } from './InsightsCardView.js';
import { InsightsDrillModal, type DrillTarget } from './InsightsDrillModal.js';
import { InsightsMarkdown } from './InsightsMarkdown.js';
import { MemberLens } from './MemberLens.js';

type StudioTab = 'ask' | 'members';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  cards?: InsightsCard[];
  followUps?: string[];
  /** Stored message id (present after persistence — the feedback target). */
  id?: string;
  feedback?: 'up' | 'down' | null;
}

function toTurn(message: InsightsChatTurn): Turn {
  return {
    role: message.role,
    content: message.content,
    cards: message.cards,
    followUps: message.followUps,
    id: message.id,
    feedback: message.feedback,
  };
}

/** Hides the machine-parsed FOLLOWUPS line while the narrative is still streaming in. */
function stripFollowUpsLine(content: string): string {
  const index = content.indexOf('FOLLOWUPS:');
  if (index < 0) return content;
  return content.slice(0, index).trimEnd();
}

/** Stable pin identity (card ids restart at card-1 every turn). */
function pinKey(card: InsightsCard): string {
  return `${card.id}|${card.title}`;
}

/** The combined concrete date window across a turn's cards. */
function cardWindow(cards: InsightsCard[]): { from?: string; to?: string } | null {
  let from: string | undefined;
  let to: string | undefined;
  for (const card of cards) {
    const f = card.result.spec.filters;
    if (f.dateFrom && (!from || f.dateFrom < from)) from = f.dateFrom;
    if (f.dateTo && (!to || f.dateTo > to)) to = f.dateTo;
  }
  return from || to ? { from, to } : null;
}

/** "3 queries · 12,400 records scanned · window 2026-05-10 → 2026-08-07". */
function traceLine(cards: InsightsCard[]): string {
  const scanned = cards.reduce((sum, c) => sum + c.result.scannedEntries, 0);
  const parts = [
    `${cards.length} ${cards.length === 1 ? 'query' : 'queries'}`,
    `${scanned.toLocaleString()} records scanned`,
  ];
  const window = cardWindow(cards);
  if (window) parts.push(`window ${window.from ?? '…'} → ${window.to ?? 'today'}`);
  return parts.join(' · ');
}

const STARTER_QUESTIONS = [
  'What did the team spend time on in the last 90 days?',
  'How many open tickets do we have by status, and how old are they?',
  'How much unbilled value is sitting on each customer right now?',
  'Show the pipeline by stage with value and win rate this year',
];

/**
 * Insights Studio — conversational AI analytics over time entry data. Ask a question,
 * get a narrative + interactive charts, drill into the underlying entries, and follow
 * the AI's suggested next questions. The Members tab profiles one member's activity.
 */
export function InsightsStudioPage(): JSX.Element {
  const statusQuery = useInsightsStudioStatus();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<StudioTab>('ask');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillTarget | null>(null);
  const [pinned, setPinned] = useState<InsightsCard[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const conversationsQuery = useInsightsConversations();
  const conversationQuery = useInsightsConversation(activeId);
  const renameMutation = useRenameInsightsConversation();
  const deleteMutation = useDeleteInsightsConversation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<InsightsConversationSummary | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, busy]);

  // Re-render a stored conversation (cards replay from the DB — no model call).
  // Syncs only when the stored copy changes so optimistic turns are never clobbered
  // by a stale cache right after a send.
  useEffect(() => {
    if (!activeId) {
      setTurns([]);
      return;
    }
    const detail = conversationQuery.data;
    if (!detail || detail.id !== activeId || busy) return;
    setTurns(detail.messages.map(toTurn));
  }, [activeId, conversationQuery.data]);

  const status = statusQuery.data;
  const startersQuery = useInsightsStarters(Boolean(status?.allowed && status?.available));
  const savedQuestionsQuery = useSavedInsightsQuestions(
    Boolean(status?.allowed && status?.available),
  );
  const saveQuestionMutation = useSaveInsightsQuestion();
  const deleteSavedQuestionMutation = useDeleteSavedInsightsQuestion();
  const savedQuestions = savedQuestionsQuery.data ?? [];
  const starterQuestions = startersQuery.data?.questions?.length
    ? startersQuery.data.questions
    : STARTER_QUESTIONS;
  const conversations = conversationsQuery.data?.data ?? [];

  const rateTurn = (index: number, feedback: 'up' | 'down'): void => {
    const turn = turns[index];
    if (!turn?.id) return;
    const next = turn.feedback === feedback ? null : feedback;
    setTurns((prev) => prev.map((t, i) => (i === index ? { ...t, feedback: next } : t)));
    void setInsightsFeedback(turn.id, next).catch(() => {
      // Rating is best-effort UI sugar — revert quietly on failure.
      setTurns((prev) =>
        prev.map((t, i) => (i === index ? { ...t, feedback: turn.feedback ?? null } : t)),
      );
    });
  };

  const togglePin = (card: InsightsCard): void =>
    setPinned((prev) =>
      prev.some((c) => pinKey(c) === pinKey(card))
        ? prev.filter((c) => pinKey(c) !== pinKey(card))
        : [...prev, card],
    );
  const isPinned = (card: InsightsCard): boolean =>
    pinned.some((c) => pinKey(c) === pinKey(card));

  // The window behind the most recent answer (shown in the footer for trust).
  const lastWindow = (() => {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      const turn = turns[i]!;
      if (turn.role === 'assistant' && turn.cards?.length) {
        const window = cardWindow(turn.cards);
        if (window) return window;
      }
    }
    return null;
  })();

  const startNewChat = (): void => {
    if (busy) return;
    setActiveId(null);
    setTurns([]);
    setError(null);
  };

  const selectConversation = (id: string): void => {
    if (busy || id === activeId) return;
    setActiveId(id);
    setError(null);
  };

  const beginRename = (conversation: InsightsConversationSummary): void => {
    setEditingId(conversation.id);
    setEditTitle(conversation.title);
  };

  const commitRename = (): void => {
    const id = editingId;
    const title = editTitle.trim();
    setEditingId(null);
    if (!id || !title) return;
    renameMutation.mutate({ id, title });
  };

  const confirmDelete = (): void => {
    const target = deleteTarget;
    if (!target) return;
    deleteMutation.mutate(target.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        if (target.id === activeId) startNewChat();
      },
    });
  };

  const ask = async (question: string): Promise<void> => {
    const text = question.trim();
    if (!text || busy) return;
    setError(null);
    setProgress(null);
    // Optimistic user turn + a live assistant turn that streams in (tokens + cards).
    setTurns((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', cards: [] },
    ]);
    setInput('');
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const updateLast = (update: (turn: Turn) => Turn): void =>
      setTurns((prev) => prev.map((t, i) => (i === prev.length - 1 ? update(t) : t)));
    try {
      const response = await sendInsightsChatStream(
        { conversationId: activeId, message: text },
        {
          onToken: (t) => {
            setProgress(null);
            updateLast((turn) => ({ ...turn, content: turn.content + t }));
          },
          onToolStart: (label) => setProgress(label),
          onCard: (card) =>
            updateLast((turn) => ({ ...turn, cards: [...(turn.cards ?? []), card] })),
        },
        controller.signal,
      );
      // Swap the streamed draft for the canonical reply (figure-guard rounds and the
      // FOLLOWUPS line are already cleaned server-side).
      updateLast(() => ({
        role: 'assistant',
        content: response.reply,
        cards: response.cards,
        followUps: response.followUps,
        id: response.messageId,
        feedback: null,
      }));
      if (!activeId) setActiveId(response.conversationId);
      void queryClient.invalidateQueries({ queryKey: insightsKeys.conversations });
      void queryClient.invalidateQueries({
        queryKey: insightsKeys.conversation(response.conversationId),
      });
    } catch (err) {
      if (controller.signal.aborted) {
        // The server persisted the partial turn; keep what streamed in.
        updateLast((turn) =>
          turn.content ? turn : { ...turn, content: '(Stopped before an answer was produced.)' },
        );
        void queryClient.invalidateQueries({ queryKey: insightsKeys.conversations });
      } else {
        // Drop an empty placeholder so a failed send leaves no blank bubble.
        setTurns((prev) =>
          prev.filter(
            (t, i) =>
              !(i === prev.length - 1 && t.role === 'assistant' && !t.content && !t.cards?.length),
          ),
        );
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error && err.message
              ? err.message
              : 'Something went wrong. Please try again.';
        setError(message);
      }
    } finally {
      setProgress(null);
      setBusy(false);
      abortRef.current = null;
    }
  };

  const stopStreaming = (): void => abortRef.current?.abort();

  const openDrill = (domain: InsightsDomain, filters: InsightsQueryFilters, title: string): void =>
    setDrill({ domain, filters, title });

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Insights Studio"
        description="AI-driven analysis of time entry data — ask anything, get charts, drill into the hours behind them."
      />

      {status && !status.allowed ? (
        <Notice text="Your role does not include Insights Studio. Ask an administrator to grant the Insights Studio menu item to your role." />
      ) : status && !status.available ? (
        <Notice text="Insights Studio needs the AI assistant configured and enabled (Integrations › AI Assistant)." />
      ) : (
        <>
          <TabNav
            tabs={[
              { id: 'ask', label: 'Ask' },
              { id: 'members', label: 'Member lens' },
            ]}
            activeId={tab}
            onChange={(id) => setTab(id as StudioTab)}
          />

          {tab === 'members' ? (
            <MemberLens onDrill={setDrill} />
          ) : (
            <div className="flex min-h-0 flex-1 gap-4">
              <aside className="flex w-60 shrink-0 flex-col border-r border-slate-200 pr-3">
                <button
                  type="button"
                  onClick={startNewChat}
                  disabled={busy}
                  className="mb-3 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
                >
                  + New chat
                </button>
                <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                  {conversations.length === 0 && !conversationsQuery.isLoading ? (
                    <p className="px-2 pt-2 text-xs text-slate-400">
                      Your chats will appear here.
                    </p>
                  ) : null}
                  {conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 ${
                        conversation.id === activeId
                          ? 'bg-brand-50 text-brand-800'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {editingId === conversation.id ? (
                        <input
                          autoFocus
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onBlur={commitRename}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          maxLength={60}
                          className="w-full rounded border border-brand-300 bg-white px-1.5 py-0.5 text-xs focus:outline-none"
                        />
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => selectConversation(conversation.id)}
                            className="min-w-0 flex-1 truncate text-left text-xs font-medium"
                            title={conversation.title}
                          >
                            {conversation.title}
                          </button>
                          <button
                            type="button"
                            onClick={() => beginRename(conversation)}
                            className="hidden shrink-0 rounded p-1 text-slate-400 hover:text-slate-700 group-hover:block"
                            title="Rename"
                          >
                            <Icon name="pencil" className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(conversation)}
                            className="hidden shrink-0 rounded p-1 text-slate-400 hover:text-rose-600 group-hover:block"
                            title="Delete"
                          >
                            <Icon name="trash" className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>

              <div className="flex min-h-0 flex-1 flex-col">
                {pinned.length > 0 ? (
                  <div className="mb-3 grid max-h-96 gap-3 overflow-y-auto lg:grid-cols-2">
                    {pinned.map((card) => (
                      <InsightsCardView
                        key={pinKey(card)}
                        card={card}
                        onDrill={openDrill}
                        pinned
                        onTogglePin={togglePin}
                      />
                    ))}
                  </div>
                ) : null}
                {activeId && conversationQuery.isLoading && turns.length === 0 ? (
                  <div className="flex items-center gap-2 pt-4 text-xs text-slate-500">
                    <Spinner /> Loading conversation…
                  </div>
                ) : null}
                <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-4 pr-1">
                {turns.length === 0 ? (
                  <div className="mx-auto max-w-2xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                    <Icon name="assistant" className="mx-auto h-8 w-8 text-brand-600" />
                    <h2 className="mt-2 text-sm font-semibold text-slate-900">
                      Ask anything about your team&rsquo;s time
                    </h2>
                    <p className="mt-1 text-xs text-slate-500">
                      The AI runs safe, read-only queries and answers with charts you can drill
                      into. Try one of these:
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {starterQuestions.map((q) => (
                        <Chip key={q} label={q} onClick={() => void ask(q)} />
                      ))}
                    </div>
                    {savedQuestions.length > 0 ? (
                      <div className="mt-5 border-t border-slate-100 pt-4">
                        <p className="text-xs font-semibold text-slate-600">Your saved questions</p>
                        <div className="mt-2 flex flex-wrap justify-center gap-2">
                          {savedQuestions.map((saved) => (
                            <span key={saved.id} className="inline-flex items-center gap-1">
                              <Chip label={saved.question} onClick={() => void ask(saved.question)} />
                              <button
                                type="button"
                                onClick={() => deleteSavedQuestionMutation.mutate(saved.id)}
                                className="rounded p-0.5 text-slate-400 hover:text-rose-600"
                                title="Delete saved question"
                              >
                                <Icon name="trash" className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {turns.map((turn, i) =>
                  turn.role === 'user' ? (
                    <div key={i} className="group flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => saveQuestionMutation.mutate(turn.content)}
                        className="hidden shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50 group-hover:block"
                        title="Save this question to re-run later"
                      >
                        Save
                      </button>
                      <p className="max-w-xl rounded-2xl rounded-br-sm bg-brand-600 px-4 py-2 text-sm text-white">
                        {turn.content}
                      </p>
                    </div>
                  ) : (
                    <div key={i} className="space-y-3">
                      {turn.cards?.map((card) => (
                        <InsightsCardView
                          key={card.id}
                          card={card}
                          onDrill={openDrill}
                          pinned={isPinned(card)}
                          onTogglePin={togglePin}
                        />
                      ))}
                      {turn.content ? (
                        <div className="max-w-3xl rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-4 py-3 shadow-sm">
                          <InsightsMarkdown text={stripFollowUpsLine(turn.content)} />
                          {turn.cards?.length || turn.id ? (
                            <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-100 pt-1.5">
                            <p className="text-[11px] text-slate-400">
                              {turn.cards?.length ? traceLine(turn.cards) : ''}
                            </p>
                            {turn.id ? (
                              <div className="flex shrink-0 gap-1">
                                <button
                                  type="button"
                                  onClick={() => rateTurn(i, 'up')}
                                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                    turn.feedback === 'up'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                  }`}
                                  title="Good answer"
                                >
                                  Helpful
                                </button>
                                <button
                                  type="button"
                                  onClick={() => rateTurn(i, 'down')}
                                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                    turn.feedback === 'down'
                                      ? 'bg-rose-100 text-rose-700'
                                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                  }`}
                                  title="Wrong or unhelpful answer"
                                >
                                  Not helpful
                                </button>
                              </div>
                            ) : null}
                          </div>
                          ) : null}
                        </div>
                      ) : null}
                      {turn.followUps && turn.followUps.length > 0 && i === turns.length - 1 ? (
                        <div className="flex flex-wrap gap-2">
                          {turn.followUps.map((q) => (
                            <Chip key={q} label={q} onClick={() => void ask(q)} />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ),
                )}

                {busy ? (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Spinner /> {progress ?? 'Analyzing…'}
                  </div>
                ) : null}
                {error ? (
                  <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</p>
                ) : null}
              </div>

              <div className="border-t border-slate-200 pt-3">
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void ask(input);
                  }}
                >
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask about time entries — members, customers, projects, trends…"
                    className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none"
                    disabled={busy}
                  />
                  {busy ? (
                    <button
                      type="button"
                      onClick={stopStreaming}
                      className="rounded-xl border border-rose-300 bg-rose-50 px-5 py-2.5 text-sm font-medium text-rose-700 hover:bg-rose-100"
                    >
                      Stop
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={input.trim().length === 0}
                      className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      Ask
                    </button>
                  )}
                </form>
                <p className="mt-1.5 text-[11px] text-slate-400">
                  Read-only · answers come from live PSA data via safe, whitelisted queries.
                  {lastWindow
                    ? ` · Window used: ${lastWindow.from ?? '…'} → ${lastWindow.to ?? 'today'}`
                    : ''}
                </p>
              </div>
              </div>
            </div>
          )}
        </>
      )}

      <InsightsDrillModal target={drill} onClose={() => setDrill(null)} />
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete chat"
        message={`Delete "${deleteTarget?.title ?? ''}"? Its questions, answers and charts will no longer be listed.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        error={
          deleteMutation.error
            ? deleteMutation.error instanceof ApiError
              ? deleteMutation.error.message
              : 'Failed to delete the chat.'
            : null
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function Chip({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100"
    >
      {label}
    </button>
  );
}

function Notice({ text }: { text: string }): JSX.Element {
  return <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{text}</p>;
}
